import { useState } from 'react';
import { ChevronLeftIcon } from '../components/Icons';
import { toast } from '../toast';

export default function DeleteProfilePage({ onNavigate }) {
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = (e) => {
    e.preventDefault();
    if (!confirmed) {
      toast.error('Please confirm that you understand the consequences of deleting your profile.');
      return;
    }

    setIsDeleting(true);

    setTimeout(() => {
      // Clear user data from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('mb_cart') || key.startsWith('mb_addresses')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem('mb_products'); // Reset products to initial
      localStorage.removeItem('mb_orders'); // Reset orders if any exist

      // Show alert and redirect
      toast.success('Your profile has been successfully deleted.');
      setIsDeleting(false);
      
      // Navigate to signup/login or home page
      onNavigate('signup');
      window.location.reload(); // Reload to reset all state memory
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#FAF3F0]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 lg:px-8 pt-5 pb-4 bg-[#FAF3F0]">
        <button onClick={() => onNavigate('account')} className="p-1.5 -ml-1.5 text-[#F47B20]">
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#1A1A2E]">Delete Profile</h1>
      </div>

      <div className="px-4 lg:px-8 pb-28 lg:pb-12 max-w-xl">
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 text-red-500 mb-4">
            <svg className="w-8 h-8 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-black text-[#1A1A2E]">Warning: This action is permanent</h2>
          </div>

          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            Deleting your profile will permanently remove all of your account data. You will immediately lose access to:
          </p>

          <ul className="space-y-2.5 mb-6 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              Your saved Delivery Addresses
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              Your Shopping Cart and Wishlist items
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              Your ongoing and past Order History
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">•</span>
              Access to order updates and support chat
            </li>
          </ul>

          <form onSubmit={handleDelete} className="space-y-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Why are you deleting your profile? (Optional)
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] transition-colors"
              >
                <option value="">Select a reason</option>
                <option value="no_longer_needed">I no longer need bridal items</option>
                <option value="privacy_concerns">Privacy concerns</option>
                <option value="difficult_to_use">The application was difficult to use</option>
                <option value="other">Other reason</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Please enter your password to confirm
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] transition-colors"
              />
            </div>

            <div className="flex items-start gap-2.5 pt-2">
              <input
                type="checkbox"
                id="confirmCheckbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="w-4 h-4 rounded text-red-500 focus:ring-red-500/30 border-gray-300 mt-0.5"
              />
              <label htmlFor="confirmCheckbox" className="text-sm text-[#1A1A2E] font-semibold select-none leading-snug cursor-pointer">
                I understand this is permanent and want to delete my profile now.
              </label>
            </div>

            <div className="flex gap-3 pt-6">
              <button
                type="button"
                onClick={() => onNavigate('account')}
                disabled={isDeleting}
                className="flex-1 py-3.5 rounded-xl border border-gray-200 text-[#1A1A2E] text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!confirmed || isDeleting}
                className="flex-1 py-3.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-sm font-bold transition-colors shadow-sm disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete Permanently'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
